import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import Section, { SectionHead } from "../layout/Section";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import { SITE_CONTENT } from "../../constants/siteContent";
import { EXTERNAL_LINK_PROPS } from "../../utils/contact";
import styles from "./ZetaPage.module.css";

/*
 * /zeta — the download page for Zeta, Appinfigo's own app.
 *
 * *** WHAT THIS PAGE MAY AND MAY NOT SAY ***
 * Every price, delivery time and offer lives in the ARTWORK, never in the page's
 * own text. The creatives are dated assets that can be swapped in one commit;
 * a number typed into a string here would quietly go stale and become a claim
 * Appinfigo no longer honours. So: no "10 minutes", no "free over 149", no
 * install counts or ratings scraped from the store listing.
 *
 * Everything rendered comes from SITE_CONTENT.ownProduct (and .promo), so the
 * homepage section and this page can never disagree about the product. The h1
 * is promo.headline verbatim — the founder's own line; this file only chooses
 * which ONE word of it sets in the serif (HEADLINE_ACCENT) and adds nothing.
 *
 * The store badge only renders when its URL is set — see OwnProduct.js for the
 * same rule. An empty URL renders nothing, never a dead link.
 *
 * Surfaces: paper hero -> paper body (highlights, gallery, the four apps as
 * olive tickets) -> an inset olive PANEL for the closing download block.
 * <Section> owns every surface, the rhythm and the container. The site header
 * is fixed and transparent with no bar of its own, so the hero reserves
 * var(--header-h) above its copy (.hero in the module).
 */

const PRODUCT = SITE_CONTENT.ownProduct || {};
const PROMO = PRODUCT.promo || {};

const PLAY_URL =
  typeof PRODUCT.playStoreUrl === "string" ? PRODUCT.playStoreUrl.trim() : "";
const IS_LIVE = PRODUCT.status === "live" && Boolean(PLAY_URL);
const PLAY_BADGE = `${process.env.PUBLIC_URL}/images/products/google-play-badge.png`;
const QR_SRC = PROMO.qr ? `${process.env.PUBLIC_URL}${PROMO.qr}` : "";

const HIGHLIGHTS = (Array.isArray(PROMO.highlights) ? PROMO.highlights : []).filter(
  (item) => item && item.title
);
const GALLERY = (Array.isArray(PROMO.gallery) ? PROMO.gallery : []).filter(
  (item) => item && item.file
);
const HERO_SHOT = PROMO.hero && PROMO.hero.file ? PROMO.hero : null;
const shotSrc = (file) => `${process.env.PUBLIC_URL}/images/zeta/${file}`;
const APPS = (Array.isArray(PRODUCT.apps) ? PRODUCT.apps : []).filter(
  (app) => app && app.name
);
const STACK = (Array.isArray(PRODUCT.stack) ? PRODUCT.stack : []).filter(
  (tech) => typeof tech === "string" && tech.trim()
);

/* "Zeta by Appinfigo" — the product's own name + byline, nothing added. */
const EYEBROW = [PRODUCT.name, PRODUCT.byline].filter(Boolean).join(" ");

// TODO(content): the ONE word of promo.headline that sets in the serif accent.
// If the headline is rewritten and no longer contains it, the h1 simply renders
// without an accent — the copy is never altered to fit.
const HEADLINE_ACCENT = "faster";

/* TODO(content): section labels. Structural only — they name a block of the
   page; none of them states a price, a delivery time or an offer. The <em>
   marks the word that sets in the serif accent. */
const LABELS = {
  highlights: {
    eyebrow: "In the app",
    title: (
      <>
        What you can do <em>with Zeta</em>
      </>
    )
  },
  gallery: {
    eyebrow: "A look inside",
    title: (
      <>
        Zeta, screen by <em>screen</em>
      </>
    )
  },
  apps: {
    eyebrow: "Behind it",
    title: (
      <>
        Four apps and the <em>backend</em> behind them
      </>
    )
  },
  city: "Delivering in",
  qrHost: "Google Play",
  stack: "Built with",
  policies: "App policies",
  builtBy: "Zeta is built and run by Appinfigo.",
  talk: "Talk to us"
};

/* The three Zeta legal documents are static HTML outside the SPA, so they are
   plain anchors on purpose — a react-router <Link> would try to handle them
   client-side and land on the not-found route. */
const LEGAL = [
  { href: "/zeta/privacy-policy", label: "Privacy policy" },
  { href: "/zeta/terms-of-use", label: "Terms of use" },
  { href: "/zeta/delete-account", label: "Delete your account" }
];

function staggerDelay(index) {
  return Math.min(index, 3) * 60;
}

/* Wraps the first occurrence of `word` in <em> (the serif accent) and returns
   the text otherwise untouched — same characters, same order, same case. No
   match, no accent. */
function withAccent(text, word) {
  const source = typeof text === "string" ? text : "";
  const needle = typeof word === "string" ? word.trim() : "";
  const at = needle ? source.toLowerCase().indexOf(needle.toLowerCase()) : -1;

  if (at < 0) return source;

  return (
    <>
      {source.slice(0, at)}
      <em>{source.slice(at, at + needle.length)}</em>
      {source.slice(at + needle.length)}
    </>
  );
}

/* Google's official badge, self-hosted so the page makes no third-party
   request. Per the badge guidelines it is used unmodified and links to the
   listing. */
function PlayBadge({ className }) {
  if (!IS_LIVE) return null;
  return (
    <a
      className={`${styles.storeBadge} ${className || ""}`}
      href={PLAY_URL}
      data-analytics-interaction="app_store_click:google_play"
      {...EXTERNAL_LINK_PROPS}
    >
      <img
        src={PLAY_BADGE}
        alt="Get Zeta on Google Play"
        width="646"
        height="250"
        decoding="async"
      />
    </a>
  );
}

export default function ZetaPage() {
  /* A creative that has not been dropped into public/images/zeta/ yet must not
     leave a broken frame, and if NONE of them are present the whole gallery
     block is suppressed rather than rendering an empty heading. */
  const [broken, setBroken] = useState({});
  const markBroken = useCallback((file) => {
    setBroken((prev) => (prev[file] ? prev : { ...prev, [file]: true }));
  }, []);

  const shots = GALLERY.filter((item) => !broken[item.file]);
  const hasHeroShot = Boolean(HERO_SHOT) && !broken[HERO_SHOT.file];

  return (
    <main>
      {/* ---------------------------------------------------------- hero -- */}
      <Section tone="paper" size="lg" id="zeta-download">
        <div className={`${styles.hero} ${hasHeroShot ? "" : styles.heroSolo}`}>
          <div className={styles.heroCopy}>
            {/* No standalone logo here: the hero creative already carries the
                Zeta lockup, and repeating it pushed the headline down the page
                for no gain. The "live" tag is a FILLED lime badge with ink
                text (13.94:1) — lime is never a text colour on paper. */}
            <Reveal className={styles.eyebrowRow} direction="up">
              {EYEBROW ? (
                <MicroLabel as="p" className={styles.eyebrowLabel}>
                  {EYEBROW}
                </MicroLabel>
              ) : null}
              {IS_LIVE && PRODUCT.launchLabel ? (
                <p className={styles.liveBadge} data-surface="lime">
                  <Icon name="check" size={16} />
                  <MicroLabel>{PRODUCT.launchLabel}</MicroLabel>
                </p>
              ) : null}
            </Reveal>

            {/* The Reveal is a wrapper, not the h1 itself: Statement keeps its
                own element and the h1 stays free of animation classes. */}
            <Reveal direction="up" delay={60}>
              <Statement as="h1" size="h2" className={styles.headline}>
                {withAccent(PROMO.headline, HEADLINE_ACCENT)}
              </Statement>
            </Reveal>

            {PROMO.sub ? (
              <Reveal as="p" className={styles.sub} direction="up" delay={120}>
                {PROMO.sub}
              </Reveal>
            ) : null}

            {/* Badge and QR sit together: the badge is for someone already on a
                phone, the QR for someone reading this on a desktop. */}
            <Reveal className={styles.actions} direction="up" delay={180}>
              <div className={styles.actionsMain}>
                <PlayBadge />
                {PRODUCT.firstCity ? (
                  <p className={styles.city}>
                    <Icon name="pin" size={16} />
                    <span>
                      {LABELS.city} {PRODUCT.firstCity}
                    </span>
                  </p>
                ) : null}
              </div>

              {QR_SRC && IS_LIVE ? (
                <TicketCard tone="paper" className={styles.qrCard}>
                  <img
                    className={styles.qr}
                    src={QR_SRC}
                    alt="QR code linking to Zeta on Google Play"
                    width="512"
                    height="512"
                    decoding="async"
                  />
                  <p className={styles.qrCaption}>
                    <MicroLabel>{PROMO.qrCaption}</MicroLabel>
                    <MicroLabel className={styles.qrHost}>{LABELS.qrHost}</MicroLabel>
                  </p>
                </TicketCard>
              ) : null}
            </Reveal>
          </div>

          {/* The flagship creative, unframed: no border, no shadow — only the
              panel radius. */}
          {hasHeroShot ? (
            <Reveal className={styles.heroShot} direction="up" delay={120}>
              <img
                src={shotSrc(HERO_SHOT.file)}
                alt={HERO_SHOT.alt || "Zeta app"}
                fetchpriority="high"
                decoding="async"
                onError={() => markBroken(HERO_SHOT.file)}
              />
            </Reveal>
          ) : null}
        </div>
      </Section>

      {/* ---------------------------------------------------- highlights -- */}
      {HIGHLIGHTS.length ? (
        <Section tone="paper" size="std">
          <SectionHead
            eyebrow={LABELS.highlights.eyebrow}
            title={LABELS.highlights.title}
          />
          {/* role="list" is redundant per spec but load-bearing: Safari drops
              list semantics once list-style is none. */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul className={styles.highlights} role="list">
            {HIGHLIGHTS.map((item, index) => (
              <Reveal
                as="li"
                key={item.title}
                className={styles.highlight}
                direction="up"
                delay={staggerDelay(index)}
              >
                <Icon name={item.icon} size={26} className={styles.highlightIcon} />
                {/* h3: these sit under the section's h2, and an h2 here would
                    also be clip-wiped by HeadingWipe as if it were a section
                    heading. */}
                <h3 className={styles.highlightTitle}>{item.title}</h3>
                {item.detail ? (
                  <p className={styles.highlightDetail}>{item.detail}</p>
                ) : null}
              </Reveal>
            ))}
          </ul>
        </Section>
      ) : null}

      {/* ------------------------------------------------------- gallery -- */}
      {shots.length ? (
        <Section tone="paper" size="lg" id="zeta-gallery">
          <SectionHead eyebrow={LABELS.gallery.eyebrow} title={LABELS.gallery.title} />
          <div className={styles.gallery}>
            {shots.map((shot, index) => (
              <Reveal
                key={shot.file}
                className={styles.shot}
                direction="up"
                delay={staggerDelay(index)}
              >
                <img
                  src={shotSrc(shot.file)}
                  alt={shot.alt || "Zeta app promotional image"}
                  loading="lazy"
                  decoding="async"
                  onError={() => markBroken(shot.file)}
                />
              </Reveal>
            ))}
          </div>
        </Section>
      ) : null}

      {/* ---------------------------------------------------------- apps -- */}
      {/* Olive tickets on the paper sheet. Each TicketCard is its own surface:
          it re-declares the --section-* context, so its heading is bone and its
          copy bone-soft with no colour set here. */}
      {APPS.length ? (
        <Section tone="paper" size="std">
          <SectionHead
            eyebrow={LABELS.apps.eyebrow}
            title={LABELS.apps.title}
            lead={PRODUCT.why}
          />
          {/* role="list": see the note on the highlights list. */}
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
          <ul className={styles.apps} role="list">
            {APPS.map((app, index) => (
              <Reveal
                as="li"
                key={app.name}
                className={styles.appItem}
                direction="up"
                delay={staggerDelay(index)}
              >
                <TicketCard tone="olive" className={styles.app}>
                  <Icon name={app.icon} size={24} className={styles.appIcon} />
                  <h3 className={styles.appName}>{app.name}</h3>
                  {app.detail ? <p className={styles.appDetail}>{app.detail}</p> : null}
                </TicketCard>
              </Reveal>
            ))}
          </ul>

          {STACK.length ? (
            <p className={styles.stack}>
              <MicroLabel className={styles.stackLabel}>{LABELS.stack}</MicroLabel>
              {STACK.join(" · ")}
            </p>
          ) : null}
        </Section>
      ) : null}

      {/* -------------------------------------------------------- closing -- */}
      {/* The page's CTA: an inset olive panel. The h2 is a Statement, so
          HeadingWipe (App.js) animates it — it is never wrapped in a Reveal. */}
      <Section tone="olive" panel size="lg" id="zeta-get">
        <div className={styles.closing}>
          <div className={styles.closingMain}>
            <Statement as="h2" size="h2">
              {withAccent(PROMO.downloadLabel, PRODUCT.name)}
            </Statement>
            {PROMO.sub ? <p className={styles.closingSub}>{PROMO.sub}</p> : null}
            <PlayBadge className={styles.closingBadge} />
          </div>

          <div className={styles.closingSide}>
            <MicroLabel as="p" className={`micro--accent ${styles.legalLabel}`}>
              {LABELS.policies}
            </MicroLabel>
            {/* role="list": see the note on the highlights list. */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul className={styles.legal} role="list">
              {LEGAL.map((item) => (
                <li key={item.href}>
                  <a className="link-arrow" href={item.href}>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
            <p className={styles.builtBy}>
              {LABELS.builtBy}{" "}
              <Link className="link-arrow" to="/contact">
                {LABELS.talk}
              </Link>
            </p>
          </div>
        </div>
      </Section>
    </main>
  );
}
